/**
 * Ad pack (Labs): one click turns the video into a variant for every channel — the social optimisers (TikTok, Meta,
 * YouTube Shorts, Snapchat, Pinterest) get two other hooks and 15/10/6 s cuts, YouTube and LinkedIn a 16:9 version,
 * TV a broadcast spot, outdoor screens a silent cut. The server makes and renders them one after another (backend
 * pack.ts); this dialog starts it, then shows which channels are covered and lists each variant with its file and
 * whether it passes the delivery specs of the channels it was made for — the whole pack, ready for the media buyer.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Download, ExternalLink, Layers, Loader2, ShieldCheck, TriangleAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi, type VideoPackItem } from '@/cowork/omniad/api/coworkApi';
import { packItemState as itemState, saveVideoFile } from './videoStudioShared';

/** What the pack makes, in the person's words (the server leaves out what does not apply and says why). */
const MAKES = [
  'Two other hooks (from the Hook lab) and 15, 10 and 6 s cut-downs',
  '9:16 for TikTok, Reels, Shorts, Snapchat · 4:5 for the Meta and Pinterest feeds · 16:9 for YouTube and LinkedIn',
  'A TV spot (15 or 30 s, sound on) and a silent 10 s cut for outdoor screens',
  'Each one checked against the specs of every channel',
];

export function AdPackDialog({ id, creators, rightsOk, onRightsChange, onOpen, onClose }: {
  id: string;
  /** Creators whose clips are in the video: paid usage is confirmed once before anything is made into ads. */ creators: string[];
  rightsOk: boolean; onRightsChange: (ok: boolean) => void;
  onOpen: (videoId: string) => void; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [starting, setStarting] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  // Poll while anything in the pack is still being written or rendered.
  const q = useQuery({
    queryKey: ['videos', 'pack', id],
    queryFn: () => coworkApi.videos.packStatus(id),
    refetchInterval: (query) => { const d = query.state.data; return d?.pack?.status === 'making' || d?.items.some((it) => itemState(it).busy) ? 3000 : false; },
  });
  const pack = q.data?.pack || null;
  const items = q.data?.items || [];
  const coverage = q.data?.coverage || [];
  const needRights = creators.length > 0;
  const covered = coverage.filter((c) => c.covered).length;
  const done = items.filter((it) => it.render?.status === 'done').length;
  // A variant is judged on the channels it was made for (a TV spot is not meant for TikTok).
  const targeted = (it: VideoPackItem) => (it.checks || []).filter((c) => it.targets?.includes(c.platform));

  async function start() {
    setStarting(true);
    try { await coworkApi.videos.pack(id); await qc.invalidateQueries({ queryKey: ['videos', 'pack', id] }); }
    catch (e) { toast.error((e as Error).message); }
    finally { setStarting(false); }
  }
  async function save(it: VideoPackItem) {
    setSaving(it.id);
    try { toast.success(`Saved ${await saveVideoFile(it.id)}`); }
    catch (e) { toast.error((e as Error).message); }
    finally { setSaving(null); }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Ad pack" className="fixed inset-0 z-50 flex items-center justify-center bg-[#101828]/60 p-6" onClick={onClose}>
      <div className="flex max-h-full w-full max-w-[640px] flex-col gap-4 overflow-hidden rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-[15px] font-semibold text-[#101828]"><Layers className="h-4 w-4 text-[#7a1fbf]" />Ad pack for every channel<span className="rounded-full bg-[#f4e8ff] px-2 py-0.5 text-[11px] font-bold text-[#7a1fbf]">Labs</span></span>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]"><X className="h-4 w-4" /></button>
        </div>

        {q.isLoading ? <div className="grid h-32 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#98a2b3]" /></div>
        : !pack || (pack.status !== 'making' && !items.length) ? (
          // Nothing made yet: what one click gives, and the paid-usage confirmation.
          <>
            <p className="text-[13px] text-[#475467]">One video in, a version for every channel out — the variants the platforms' optimisers need to find what wins, a TV spot and an outdoor cut. Each is a video of its own; this one stays as it is.</p>
            <ul className="flex flex-col gap-2">{MAKES.map((m) => <li key={m} className="flex items-center gap-2 text-[13px] text-[#344054]"><Check className="h-4 w-4 shrink-0 text-[#3b7d0b]" />{m}</li>)}</ul>
            {pack?.status === 'failed' && <p className="rounded-xl border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-[13px] text-[#b42318]">{pack.error || 'The last pack could not be made.'}</p>}
            {needRights && (
              <label className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 ${rightsOk ? 'border-[#c6f37a] bg-[#f7fde9]' : 'border-[#fedf89] bg-[#fffcf5]'}`}>
                <input type="checkbox" checked={rightsOk} onChange={(e) => onRightsChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#3b7d0b]" />
                <span className="flex flex-col"><span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#101828]"><ShieldCheck className="h-4 w-4 text-[#3b7d0b]" />Paid usage is cleared for {creators.join(', ')}</span><span className="text-[12px] text-[#667085]">Every variant runs as an ad (Meta Partnership Ads / TikTok Spark Ads or a usage licence).</span></span>
              </label>
            )}
            <div className="flex justify-end">
              <button type="button" disabled={starting || (needRights && !rightsOk)} onClick={() => void start()} className="inline-flex items-center gap-2 rounded-lg bg-[#2e024c] px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-[#46236a] disabled:opacity-40">{starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4 text-[#a9f600]" />}Make the pack</button>
            </div>
          </>
        ) : (
          <>
            <p className="text-[13px] text-[#475467]">{pack.status === 'making' && !items.length ? 'Setting up the variants…' : `${done} of ${items.length} ready · ${covered} of ${coverage.length} channels covered.`}{pack.status === 'making' ? ' They render one after another; you can close this and come back.' : ''}</p>
            {coverage.length > 0 && (
              // Which channels the pack already serves: a rendered variant passes that channel's specs.
              <div className="flex flex-wrap gap-1.5">{coverage.map((c) => (
                <span key={c.platform} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${c.covered ? 'bg-[#f0fbdc] text-[#3b7d0b]' : 'bg-[#f2f4f7] text-[#667085]'}`}>{c.covered ? <Check className="h-3 w-3" /> : <Loader2 className={`h-3 w-3 ${pack.status === 'making' ? 'animate-spin' : 'hidden'}`} />}{c.name}</span>
              ))}</div>
            )}
            <ul className="flex min-h-0 flex-col divide-y divide-[#eaecf0] overflow-y-auto rounded-xl border border-[#eaecf0]">
              {items.map((it) => {
                const st = itemState(it);
                return (
                  <li key={it.id} className="flex flex-col gap-1.5 px-3 py-2.5">
                    <div className="flex items-center gap-3">
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-[13px] font-semibold text-[#101828]">{it.label}</span>
                        <span className={`flex items-center gap-1.5 text-[12px] ${st.failed ? 'text-[#b42318]' : 'text-[#667085]'}`}>{st.busy && <Loader2 className="h-3 w-3 animate-spin" />}{it.aspect ? `${it.aspect} · ${it.seconds?.toFixed(1)} s · ` : ''}{st.text}</span>
                      </span>
                      {it.render?.status === 'done' && <button type="button" aria-label={`Download ${it.label}`} disabled={saving === it.id} onClick={() => void save(it)} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#d0d5dd] bg-white px-2.5 py-1 text-[12.5px] font-semibold text-[#344054] hover:bg-[#f9fafb] disabled:opacity-60">{saving === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}MP4</button>}
                      {!it.missing && <button type="button" aria-label={`Open ${it.label}`} onClick={() => onOpen(it.id)} className="shrink-0 rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7]"><ExternalLink className="h-4 w-4" /></button>}
                    </div>
                    {/* The channels this variant is for, on their own row (a 9:16 cut serves five). */}
                    <div className="flex flex-wrap gap-1.5">
                        {targeted(it).map((c) => (
                          <span key={c.platform} title={c.ok ? `Passes ${c.name} specs` : c.fails.join(' ')} className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${c.ok ? 'bg-[#f0fbdc] text-[#3b7d0b]' : 'bg-[#fef0c7] text-[#93370d]'}`}>
                            {c.ok ? <Check className="h-3 w-3" /> : <TriangleAlert className="h-3 w-3" />}{c.name}
                          </span>
                        ))}
                    </div>
                    {targeted(it).filter((c) => !c.ok).map((c) => <span key={c.platform} className="text-[12px] text-[#93370d]">{c.name}: {c.fails.join(' ')}</span>)}
                  </li>
                );
              })}
            </ul>
            {pack.notes.length > 0 && <p className="text-[12px] text-[#667085]">{pack.notes.join(' ')}</p>}
            {pack.status !== 'making' && (
              <div className="flex justify-end">
                <button type="button" disabled={starting} onClick={() => void start()} className="rounded-lg border border-[#d0d5dd] bg-white px-3 py-1.5 text-[12.5px] font-semibold text-[#344054] hover:bg-[#f9fafb] disabled:opacity-40">Make a new pack</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
